import { api, cachedRequest } from '../apiClient';
import { Cart } from '../../types';

export const paymentService = {
  // Get payment status
  getStatus: async (cartId: number): Promise<{
    success: boolean;
    cart_id: number;
    status: string;
    is_closed: boolean;
    processed: boolean;
    payment_gateway: string | null;
    total: number;
    currency: string | null;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.get(`/payment/${cartId}/status/`);
    return response.data;
  },

  // Handle payment callback
  handleCallback: async (cartId: number, gateway: string, params: Record<string, any> = {}): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url: string;
  }> => {
    const response = await api.get(`/payment/${cartId}/callback/${gateway}/`, { params });
    return response.data;
  },

  // Initialize payment processor (main API endpoint for React)
  initializePayment: async (cartId: number, gateway: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url?: string;
    form_html?: string;
    callback_url?: string;
  }> => {
    const response = await api.get(`/checkout/${cartId}/payment/${gateway}/`);
    return response.data;
  },

  // Handle payment callback (main API endpoint for React)
  handlePaymentCallback: async (cartId: number, gateway: string, params: Record<string, any> = {}): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url: string;
  }> => {
    const cacheKey = `payment_callback_${cartId}_${gateway}_${new URLSearchParams(params as any).toString()}`;
    // Use cachedRequest to dedupe concurrent/pending identical requests only; TTL very short and not forced
    return cachedRequest(
      cacheKey,
      async () => {
        const response = await api.get(`/checkout/${cartId}/${gateway}/callback/`, { params });
        return response.data;
      },
      10 * 1000, // 10s TTL to avoid caching beyond immediate duplicate window
      false
    );
  },

  // Get confirmation data after successful payment
  getConfirmation: async (cartId: number): Promise<{
    success: boolean;
    cart: Cart;
    passes: any[];
    items: any[];
    email?: string;
    phone?: string;
    message?: string;
    error?: string;
  }> => {
    const response = await api.get(`/checkout/${cartId}/confirmation/`);
    return response.data;
  },

  // Store SSLCommerz POST callback data in backend session
  storeSSLCCallback: async (cartId: number, postData: Record<string, string>): Promise<{
    success: boolean;
    message?: string;
  }> => {
    const response = await api.post(`/checkout/${cartId}/sslc/callback/`, postData);
    return response.data;
  },

  // Validate SSLCommerz payment (retrieves POST data from session)
  validateSSLC: async (cartId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url: string;
    cart_id?: number;
  }> => {
    const response = await api.post(`/checkout/${cartId}/sslc/validate/`);
    return response.data;
  },

  // Validate bKash payment
  validateBkash: async (cartId: number, paymentID: string, status: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url: string;
    cart_id?: number;
  }> => {
    const response = await api.post(`/checkout/${cartId}/bkash/validate/`, {
      paymentID,
      status
    });
    return response.data;
  },

  // Validate Pathao Pay payment
  validatePathaoPay: async (cartId: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url: string;
    cart_id?: number;
  }> => {
    const response = await api.post(`/checkout/${cartId}/pathao-pay/validate/`);
    return response.data;
  },

  // Direct payment processor endpoints (for specific gateways)
  bkash: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/bkash/`);
    return response.data;
  },
  nagad: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/nagad/`);
    return response.data;
  },
  sslc: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/sslc/`);
    return response.data;
  },
  tctp: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/tctp/`);
    return response.data;
  },
  beam: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/beam/`);
    return response.data;
  },
  omise: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/omise/`);
    return response.data;
  },

  // Omise: GET config (source types, public key)
  getOmiseConfig: async (cartId: number): Promise<{
    success: boolean;
    cart_id: number;
    amount: number;
    amount_subunit: number;
    currency: string;
    omise_public_key: string;
    source_types: Array<[string, string]>;
    error?: string;
    message?: string;
  }> => {
    const response = await api.get(`/checkout/${cartId}/payment/omise/`);
    return response.data;
  },

  // Omise: POST to create payment (returns redirect_url or qr_code_data)
  createOmisePayment: async (
    cartId: number,
    data: { source_type: string; omiseToken?: string; omiseSource?: string }
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url?: string;
    charge_id?: string;
    qr_code_data?: string;
    payment_method?: string;
    status?: string;
  }> => {
    const response = await api.post(`/checkout/${cartId}/payment/omise/`, data);
    return response.data;
  },

  // Omise: poll charge status (for PromptPay QR)
  getOmiseStatus: async (
    cartId: number,
    chargeId?: string
  ): Promise<{
    success: boolean;
    charge_id?: string;
    status?: string;
    message?: string;
    redirect_url?: string;
  }> => {
    const params = chargeId ? { charge_id: chargeId } : {};
    const response = await api.get(`/checkout/${cartId}/omise/status/`, { params });
    return response.data;
  },

  // Omise: validate payment after callback or successful poll
  validateOmise: async (
    cartId: number,
    data?: { charge_id?: string }
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    redirect_url?: string;
    cart_id?: number;
  }> => {
    const response = await api.post(`/checkout/${cartId}/omise/validate/`, data || {});
    return response.data;
  },

  pathaoPay: async (cartId: number) => {
    const response = await api.get(`/checkout/${cartId}/payment/pathao-pay/`);
    return response.data;
  },
};

